import { ensureDefined, isTrue } from '@avantia/client-and-server-utilities';
import { lodash } from '@avantia/lodash';
import jQuery from 'jquery';
import { RouteComponentProps } from 'react-router';
import { recordElapsedTime } from '../clientLogging';
import { registerConfigurationAction } from '../config/registerClientConfig';
import { createViewportState } from '../reducers/initialState';
import { createCookieStore } from '../tools/clientCookies';
import { postScriptError } from '../tools/postScriptError';
import { HpElementToProcess, processGroupElement } from '../tools/utilities';
import {
  HpClientEngineConfiguration,
  HpHistory,
  HpRedirectToErrorPageActionProps,
  HpUserInterfaceState,
  HpViewportState,
  allViewportSizeTypes
} from '../types';
import { HpFetchErrorOcurredAction } from '../types/ajaxCallActionTypes';
import { HpQuestionSetPageProps } from '../types/questionSetPagePropTypes';
import { HpHistoryWithState, pushToHistory } from './pushToHistory';

let environment: string, showViewportExtends: boolean;

registerConfigurationAction((config) => {
  ({ environment } = config as HpClientEngineConfiguration);
  showViewportExtends =
    environment === 'developer' ||
    isTrue(createCookieStore({ disableNameToKeyMapping: true }).getItem('hpShowViewportExtents'));
});

interface HpWithDispatch {
  dispatch: (err: HpFetchErrorOcurredAction) => void;
}

export type HpRunLoadDataHandlerFunction = (props: HpQuestionSetPageProps) => void;

export interface HpAddDisplaySizeClassToBodyElementProps<PageTypeT extends string> {
  userInterface: HpUserInterfaceState;
  pageType: PageTypeT;
}

export type HpAddDisplaySizeClassToBodyElementFunction<PageTypeT extends string> = (
  props: HpAddDisplaySizeClassToBodyElementProps<PageTypeT>
) => void;

export type HpMaybeShowErrorPageOrLoadDataFunction = (props: HpQuestionSetPageProps) => void;

export function redirectToErrorPageAction({
  message,
  location,
  dispatch
}: HpRedirectToErrorPageActionProps & HpWithDispatch): void {
  const { hash, pathname: path, search: query } = location;
  const detail = { path, query, hash };

  setTimeout(() => {
    // we need to wait until the window props have been set.
    postScriptError({ message, detail }, 'warn');
  }, 50);

  const action: HpFetchErrorOcurredAction = {
    type: 'FETCH_ERROR_OCCURRED',
    fetchError: message,
    description: '',
    message,
    errorDetails: detail,
    ajaxCallId: undefined,
    showErrorPage: true,
    errorCode: undefined
  };
  dispatch(action);
}

export interface HpCreatePageComponentFunctionsProps<PageTypeT extends string> {
  allPageTypes: PageTypeT[];
  runLoadDataHandler: HpRunLoadDataHandlerFunction;
}

let addDisplaySizeClassToBodyElementFunction: HpAddDisplaySizeClassToBodyElementFunction<string>;
let maybeShowErrorPageOrLoadDataFunction: HpMaybeShowErrorPageOrLoadDataFunction;

export function addDisplaySizeClassToBodyElement<PageTypeT extends string>(
  props: HpAddDisplaySizeClassToBodyElementProps<PageTypeT>
): void {
  return ensureDefined(addDisplaySizeClassToBodyElementFunction, 'addDisplaySizeClassToBodyElementFunction')(props);
}

export function maybeShowErrorPageOrLoadData<PagePropsT = HpQuestionSetPageProps>(props: PagePropsT): void {
  return ensureDefined(
    maybeShowErrorPageOrLoadDataFunction,
    'maybeShowErrorPageOrLoadDataFunction'
  )(props as HpQuestionSetPageProps);
}

export function createPageComponentFunctions<PageTypeT extends string>({
  allPageTypes,
  runLoadDataHandler
}: HpCreatePageComponentFunctionsProps<PageTypeT>): void {
  const allBodySizeClasses = allViewportSizeTypes.map((s) => `hp-${s}`);
  const allPageTypeClasses = allPageTypes.map((pt) => `hp-${pt}-page`).join(' ');

  const addDisplaySizeClassToBodyElement: HpAddDisplaySizeClassToBodyElementFunction<PageTypeT> = ({
    userInterface,
    pageType
  }) => {
    const { viewport } = userInterface;
    const { height, size, width } = viewport;
    const classToAdd = `hp-${size}`;
    const body = jQuery('body');
    if (!body.hasClass(classToAdd)) {
      body.removeClass(allBodySizeClasses).addClass(classToAdd);
    }

    if (pageType) {
      const pageClass = `hp-${pageType}-page`;
      if (!body.hasClass(pageClass)) {
        body.removeClass(allPageTypeClasses).addClass(pageClass);
      }
    }

    // allow border, etc to be added to a section with errors.
    jQuery('.hp-section').removeClass('hp-section-error');
    jQuery('.hp-section-validation').closest('.hp-section').addClass('hp-section-error');
    jQuery('[id*="~G"].av-input-error').closest('.hp-section').not('.hp-section-error').addClass('hp-section-error');

    // Add a lime border showing the extent of the viewport.
    // This is helpful when using Chrome's simulated devices.
    if (showViewportExtends) {
      jQuery('.hp-extents').remove();
      const makeLine = (t: number, l: number, w: number, h: number) =>
        `<div class="hp-extents" style="position: fixed; top: ${t}px; left: ${l}px; width: ${w}px; height: ${h}px; background-color: lime; z-index: 999" />`;

      body.append(makeLine(0, 0, 1, height));
      body.append(makeLine(0, width - 1, 1, height));
      body.append(makeLine(0, 0, width, 1));
      body.append(makeLine(height - 1, 0, width, 1));

      const boxWidth = 100;
      body.append(
        `<div class="hp-extents" style="position: fixed; top: 0px; left: ${
          width - boxWidth
        }px; width: ${boxWidth}px; text-align: right">
        <div style="display: inline-block; background-color: rgb(0, 0, 0, 0.4); padding: 5px; color: white">${width} x ${height}<br/>${size}</div>
      </div>`
      );
    }
  };

  const maybeShowErrorPageOrLoadData: HpMaybeShowErrorPageOrLoadDataFunction = (props) => {
    const { userInterface } = props;
    const { history } = props as unknown as RouteComponentProps;
    if (showErrorPage({ userInterface, history })) {
      return;
    }

    runLoadDataHandler(props);
  };

  addDisplaySizeClassToBodyElementFunction =
    addDisplaySizeClassToBodyElement as HpAddDisplaySizeClassToBodyElementFunction<string>;

  maybeShowErrorPageOrLoadDataFunction = maybeShowErrorPageOrLoadData;
}

export interface HpShowErrorPageProps {
  history: HpHistory;
  userInterface: HpUserInterfaceState;
}

export function showErrorPage({ history, userInterface }: HpShowErrorPageProps): boolean {
  const { fetchError } = userInterface;
  const { showErrorPage } = fetchError || {};
  const { hash, pathname } = location;
  if (fetchError && showErrorPage && !pathname.startsWith('/error')) {
    pushToHistory({ history: history as HpHistoryWithState, nextPath: `/error${hash}`, documentTitle: 'Error' });
    return true;
  }

  return false;
}

const checkboxLabelSelector = '.form-check.hp-checkbox-list input + .form-check-label span';
const radioLabelTextSelector = '.hp-radio-list input + .form-check-label span.av-label-text';

const checkboxAndRadioTextSelector = `${checkboxLabelSelector}, ${radioLabelTextSelector}`;

const maybeAlignCheckboxAndRadioText: MaybeAlignControlsVerticallyFunc = ({ reprocessAlignedItems }) => {
  const elements = jQuery(checkboxAndRadioTextSelector);
  for (const el of elements) {
    const element = el as HTMLElement & HpElementToProcess;
    if (!reprocessAlignedItems && element.hpLayoutFix) {
      continue;
    }

    const $element = jQuery(element);
    const height = $element.height();
    const $clonedElement = jQuery($element).clone().insertAfter($element);
    const oneLineLabelHeight = $clonedElement.html('a').height();
    if (height === oneLineLabelHeight) {
      $element.addClass('hp-single-line-alignment');
    }

    $clonedElement.remove();
    element.hpLayoutFix = true;
  }
};

const imageButtonListLabelSelector = (type: 'radio' | 'checkbox'): string =>
  `.hp-image-${type}-button-list > .av-field-section > .hp-float`;

const buttonGroupSelector = '.hp-radio-button-list > .av-field-section ';
const imageButtonListsSelector = `${imageButtonListLabelSelector('radio')}, ${imageButtonListLabelSelector(
  'checkbox'
)}`;

const maybeAddButtonListMargin: MaybeAlignControlsVerticallyFunc = ({ reprocessAlignedItems }) => {
  addButtonListMargin({
    itemGroupSelector: imageButtonListsSelector,
    itemSelector: '> label',
    reprocessAlignedItems
  });
  addButtonListMargin({
    itemGroupSelector: buttonGroupSelector,
    itemSelector: ' .hp-btn-wrapper > button',
    reprocessAlignedItems
  });
};

interface AddButtonListMarginProps {
  itemGroupSelector: string;
  itemSelector: string;
  reprocessAlignedItems?: boolean;
}

type HpCachableHtmlElement = HTMLElement & HpElementToProcess;

const getTop = (item: HpCachableHtmlElement): number | null => {
  const $item = jQuery(item);
  if ($item) {
    const position = $item.position();
    return position && position.top;
  }

  return null;
};

const removeMargin = (currentItem: HpCachableHtmlElement, isBottomRow: boolean) => {
  if (!isBottomRow) {
    if (currentItem.hpLayoutFix) {
      const marginRight = jQuery(currentItem).css('margin-right');
      jQuery(currentItem).css('margin-bottom', marginRight);
    }

    return false;
  }

  jQuery(currentItem).css('margin-bottom', 0);
  return true;
};

function addButtonListMargin({ itemGroupSelector, itemSelector, reprocessAlignedItems }: AddButtonListMarginProps) {
  const itemGroups = jQuery(itemGroupSelector);
  for (const group of itemGroups) {
    const $groupElement = jQuery(group);
    const items = $groupElement.find(itemSelector).get() as HpCachableHtmlElement[];
    processGroupElement<HpCachableHtmlElement>({
      items,
      reprocessAlignedItems: !!reprocessAlignedItems,
      getTop,
      action: removeMargin
    });
  }
}

interface MaybeAlignControlsVerticallyProps {
  reprocessAlignedItems: boolean;
}

type MaybeAlignControlsVerticallyFunc = ({ reprocessAlignedItems }: MaybeAlignControlsVerticallyProps) => void;

const maybeAlignControlsVerticallyWithoutDelay = ({
  reprocessAlignedItems = true
}: MaybeAlignControlsVerticallyProps): void => {
  recordElapsedTime('Calling maybeAlignCheckboxAndRadioText', () =>
    maybeAlignCheckboxAndRadioText({ reprocessAlignedItems })
  );
  recordElapsedTime('Calling maybeAddButtonListPadding', () => maybeAddButtonListMargin({ reprocessAlignedItems }));
};

export const maybeAlignControlsVertically: MaybeAlignControlsVerticallyFunc = lodash.debounce(
  maybeAlignControlsVerticallyWithoutDelay,
  80
);

export type HpViewportFunc = (viewport: HpViewportState) => void;
interface CheckViewportProps {
  viewport: HpViewportState;
  viewportChanged: HpViewportFunc;
}

type CheckViewportFunc = ({ viewport, viewportChanged }: CheckViewportProps) => void;

const checkViewport: CheckViewportFunc = ({ viewport, viewportChanged }) => {
  const newport = createViewportState();
  if (newport.height !== viewport.height || newport.width !== viewport.width) {
    viewportChanged(newport);
  }

  maybeAlignControlsVerticallyWithoutDelay({ reprocessAlignedItems: false });
};

export const checkViewportWithDelay: CheckViewportFunc = lodash.debounce(checkViewport, 250);
